// packages
import { index } from 'd3-array';
import { json } from 'd3-fetch';
import { derived, writable } from 'svelte/store';

// local
import { find } from './utils/array.js';

// test feeds
// import { testAg } from '../_workspace/test_data/test-ag.js';
// import { testBOE } from '../_workspace/test_data/test-BOE.js';
// import { testController } from '../_workspace/test_data/test-controller.js';
// import { testGov } from '../_workspace/test_data/test-gov.js';
// import { testHDistrict1Unexpired } from '../_workspace/test_data/test-h-district-1-unexpired.js';
// import { testHouse } from '../_workspace/test_data/test-house.js';
// import { testInsuranceCommissioner } from '../_workspace/test_data/test-insurance-commissioner.js';
// import { testLaMayor } from '../_workspace/test_data/test-la-mayor.js';
// import { testLbMayor } from '../_workspace/test_data/test-lb-mayor.js';
// import { testLtGov } from '../_workspace/test_data/test-lt-gov.js';
// import { testSos } from '../_workspace/test_data/test-sos.js';
// import { testStateAssembly } from '../_workspace/test_data/test-state-assembly.js';
// import { testStateSenate } from '../_workspace/test_data/test-state-senate.js';
// import { testSue } from '../_workspace/test_data/test-sue.js';
// import { testTreasurer } from '../_workspace/test_data/test-treasurer.js';
// import { testLA } from '../_workspace/test_data/testLA.js';

// import {
//   ATTY_GEN_URL,
//   BOE_URL,
//   CONTROLLER_URL,
//   GOV_URL,
//   H_DIST1_UNEXPIRED_URL,
//   HOUSE_URL,
//   INSURANCE_COMM_URL,
//   LA_COUNTY_URL,
//   LA_MAYOR_URL,
//   LB_MAYOR_URL,
//   LT_GOV_URL,
//   SOS_URL,
//   STATE_ASSEMBLY_URL,
//   STATE_SENATE_URL,
//   SUE_URL,
//   TREASURER_URL,
//   LA_CC_URL,
//   LA_CITY_AAG_URL,
//   LAC_AAG_URL,
// } from './constants.js';

const TEST_FEEDS = new Map(
  /** @type {[string, any][]} */ (
    [
      // [ATTY_GEN_URL, testAg],
      // [BOE_URL, testBOE],
      // [CONTROLLER_URL, testController],
      // [GOV_URL, testGov],
      // [H_DIST1_UNEXPIRED_URL, testHDistrict1Unexpired],
      // [HOUSE_URL, testHouse],
      // [INSURANCE_COMM_URL, testInsuranceCommissioner],
      // [LA_COUNTY_URL, testLA],
      // [LA_MAYOR_URL, testLaMayor],
      // [LB_MAYOR_URL, testLbMayor],
      // [LT_GOV_URL, testLtGov],
      // [SOS_URL, testSos],
      // [STATE_ASSEMBLY_URL, testStateAssembly],
      // [STATE_SENATE_URL, testStateSenate],
      // [SUE_URL, testSue],
      // [TREASURER_URL, testTreasurer],
    ]
  )
);

/**
 * @template T
 * @typedef {import('svelte/store').Readable<T>} Readable
 */

/**
 * @typedef {import('./types').RaceFeed} RaceFeed
 * @typedef {import('./types').RaceInterface} RaceInterface
 * @typedef {import('./types').RaceLookup} RaceLookup
 */

/**
 * @typedef {import('./types').DelFeed} DelFeed
 * @typedef {import('./types').DelegatesInterface} DelegatesInterface
 */

/**
 * @param {{id: string}} d
 * @returns {string}
 */
const indexById = (d) => d.id;

/**
 * @param {{ap_id: string}} d
 * @returns {string}
 */
export const indexByApId = (d) => d.ap_id;

/**
 * @param {{code: string}} d
 * @returns {string}
 */
const indexByCode = (d) => d.code;

/** @type {Map<string, Promise<Readable<RaceFeed>>>} */
const feedCache = new Map();

/**
 * Returns a polling store for the given URL, creating it only once per URL.
 * Subsequent calls with the same URL return the cached Promise so only one
 * fetch and one 30-second polling interval exist per feed, regardless of how
 * many embeds subscribe. The data inside the store still refreshes every
 * `seconds` seconds — nothing about update frequency changes.
 * @param {string} url The URL to request
 * @param {number} seconds How many seconds to wait between new requests
 * @returns {Promise<Readable<RaceFeed>>}
 */
export function createFeed(url, seconds = 30) {
  const cached = feedCache.get(url);
  if (cached) return cached;
  const promise = buildFeed(url, seconds);
  feedCache.set(url, promise);
  return promise;
}

/**
 * @param {string} url
 * @param {number} seconds
 * @returns {Promise<Readable<RaceFeed>>}
 */
async function buildFeed(url, seconds) {
  const interval = seconds * 1000;

  /** @type {number | undefined} */
  let timeoutId;

  /** @type {number} */
  let lastUpdated;

  async function makeRequest() {
    // for testing locally — comment out the early return for live/ap testing
    // const testFeed = TEST_FEEDS.get(url);
    // if (testFeed) {
    //   lastUpdated = Date.now();
    //   return testFeed;
    // }

    const feed = /** @type {RaceFeed} */ (await json(url));
    lastUpdated = Date.now();
    return feed;
  }

  const feed = await makeRequest();

  const { subscribe } = writable(feed, function start(set) {
    async function run() {
      const feed = await makeRequest();
      set(feed);
      timeoutId = window.setTimeout(run, interval);
    }

    function scheduleOrRun() {
      const elapsed = Date.now() - lastUpdated;
      if (elapsed < interval) {
        timeoutId = window.setTimeout(run, interval - elapsed);
      } else {
        run();
      }
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      } else {
        scheduleOrRun();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    scheduleOrRun();

    return function stop() {
      clearTimeout(timeoutId);
      timeoutId = undefined;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  });

  return { subscribe };
}

/**
 * @param {Readable<RaceFeed>} store
 * @param {string} office
 */
export function createOfficeFeed(store, office) {
  return derived(store, ($feed) => {
    const feed = [];

    for (let idx = 0; idx < $feed.length; idx++) {
      const item = $feed[idx];

      if (item.office === office && item.race_type != 'G') {
        feed.push(item);
      }
    }
    const sorted = feed.sort(
      (a, b) => (a.seat_number ?? 0) - (b.seat_number ?? 0)
    );
    return sorted;
    // return feed;
  });
}

/**
 * @param {Readable<RaceFeed>} store
 * @param {string} raceId
 */
export function createRaceFeed(store, raceId) {
  return derived(store, ($feed) => find($feed, (race) => race.id === raceId));
}

/**
 * @param {Readable<RaceInterface>} store
 * @param {string} candidateId
 */
export function createCandidateFeed(store, candidateId) {
  return derived(store, ($feed) =>
    find($feed.candidates, (candidate) => candidate.ap_id === candidateId)
  );
}

/**
 * @param {Readable<RaceFeed>} store
 */
export function createRaceLookup(store) {
  return derived(store, ($feed) => index($feed, indexById));
}

/**
 * @param {Readable<RaceInterface>} store
 */
export function createCandidateLookup(store) {
  return derived(store, ($race) => index($race.candidates, indexByApId));
}

/**
 * @param {Readable<RaceInterface>} store
 */
export function createSubunitLookup(store) {
  return derived(store, ($race) => index($race.subunits, indexByCode));
}

/**
 * @param {Readable<RaceLookup>} store
 * @param {string} raceId
 */

export function createRaceStore(store, raceId) {
  return derived(store, ($lookup) => $lookup.get(raceId));
}
